import { useState, useEffect, useRef, useCallback } from 'react';
import { MapBounds, Coordinates, DayForecast, DiscoverPOI } from '../types';
import { fetchWeatherForecast, fetchTourismPOIs, fetchCulturalHeritage } from '../services/discoverService';
import { detectViewportCountry } from '../utils/countryDetection';

interface DiscoverData {
  weather: DayForecast[];
  attractions: DiscoverPOI[];
  heritage: DiscoverPOI[];
  loading: boolean;
  error: boolean;
}

const EMPTY: DiscoverData = { weather: [], attractions: [], heritage: [], loading: false, error: false };

export const useDiscover = (
  bounds: MapBounds | null,
  center: Coordinates,
  enabled: boolean,
) => {
  const [data, setData] = useState<DiscoverData>(EMPTY);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastBboxRef = useRef('');

  const doFetch = useCallback(async (b: MapBounds, c: Coordinates) => {
    // Abort any previous in-flight request
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setData(prev => ({ ...prev, loading: true, error: false }));

    // Riksantikvaren (cultural heritage) is Norway-only — skip for Sweden
    const country = detectViewportCountry(b);
    const includeHeritage = country === 'NO' || country === null;

    try {
      const [weatherResult, attractionsResult, heritageResult] = await Promise.allSettled([
        fetchWeatherForecast(c.latitude, c.longitude, ac.signal),
        fetchTourismPOIs(b, ac.signal),
        includeHeritage ? fetchCulturalHeritage(b, ac.signal) : Promise.resolve([]),
      ]);

      if (ac.signal.aborted) return;

      setData({
        weather: weatherResult.status === 'fulfilled' ? weatherResult.value : [],
        attractions: attractionsResult.status === 'fulfilled' ? attractionsResult.value : [],
        heritage: heritageResult.status === 'fulfilled' ? heritageResult.value : [],
        loading: false,
        error: weatherResult.status === 'rejected' && attractionsResult.status === 'rejected' && heritageResult.status === 'rejected',
      });
    } catch {
      if (!ac.signal.aborted) {
        setData(prev => ({ ...prev, loading: false, error: true }));
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled || !bounds) {
      if (!enabled) setData(EMPTY);
      return;
    }

    // Skip if bounds haven't changed meaningfully
    const bboxKey = `${bounds.latMin.toFixed(3)}_${bounds.lonMin.toFixed(3)}_${bounds.latMax.toFixed(3)}_${bounds.lonMax.toFixed(3)}`;
    if (bboxKey === lastBboxRef.current) return;
    lastBboxRef.current = bboxKey;

    // Debounce 800ms
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      doFetch(bounds, center);
    }, 800);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, bounds, center, doFetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return data;
};
